import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import Avatar from "@/design/components/Avatar";
import CollapsibleList from "@/design/components/CollapsibleList";
import ContentLoader from "@/design/components/ContentLoader";
import FabBar from "@/design/components/FabBar";
import MasterList from "@/design/components/MasterList";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { formatToMac, toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowsRotateRight, viGear, viPhoneOffice } from "@sippy-platform/valkyrie";

import { Button, ListItem, ListItemAvatar, ListItemButton, ListItemText, useMediaQuery, useTheme } from "@mui/material";

function ProvisioningList() {
  const { mac } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [refresh, setRefresh] = useState(false);
  const provisioningStore = useSelector((state) => state.domain.provisionings);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  async function getRefresh() {
    setRefresh(true);
    toggleLoadingState(true);
    await Promise.resolve(Api.getProvisionings()).then(() => {
      setRefresh(false);
      toggleLoadingState(false);
    });
  }

  // Search every time userList or searchQuery changes
  const searchResults = useSearch(provisioningStore.list, searchValue?.replaceAll(":", ""), [
    "mac",
    "type",
    "phone",
    "sippeer.name",
    "sippeer.exten",
    "flexBot.name",
    "flexUser.name",
    "flexUser.exten",
    "serialNumber",
  ]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getProvisionings()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!mac && matchesMdUp && provisioningStore.meta.lastFetched) {
      if (provisioningStore.list.length >= 1) {
        navigate(`${provisioningStore.list[0].mac}`);
      } else {
        navigate("new");
      }
    }
  }, [mac, matchesMdUp, navigate, provisioningStore.list, provisioningStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  const getDeviceList = useMemo(() => {
    let list = searchResults ? [...searchResults] : [...provisioningStore.list];

    let listGroup = {
      lists: [
        { id: "provisioningUn", name: "Unprovisioned devices", items: [] },
        { id: "provisioning", name: "Provisioned devices", items: [] },
        { id: "provisioningFlex", name: "Active Flex Desk devices", items: [] },
        {
          id: "provisioningFlexInactive",
          name: "Inactive Flex Desk devices",
          items: [],
        },
        { id: "provisioningUnknown", name: "Unknown", items: [] },
      ],
      count: list.length,
    };

    list.forEach((item) => {
      if (item.flexDesk === 0 && !item.sippeer) {
        listGroup.lists[0].items.push(item);
      } else if (item.flexDesk === 0 && item.sippeer) {
        listGroup.lists[1].items.push(item);
      } else if (item.flexDesk === 1) {
        if (item.flexUser) {
          listGroup.lists[2].items.push(item);
        } else {
          listGroup.lists[3].items.push(item);
        }
      } else {
        listGroup.lists[4].items.push(item);
      }
    });

    return listGroup;
  }, [searchResults, provisioningStore.list]);

  return (
    <>
      <MasterList fabBar title="Provisioning" aria-label="Provisioning devices" id="provisioning-list">
        {provisioningStore.meta.count && searchResults.length >= 1 ? (
          getDeviceList.lists.map(
            (group) =>
              group.items.length > 0 && (
                <CollapsibleList name={group.name} id={group.id} count={group.items.length} key={group.id}>
                  {group.items.map((provisioning) => (
                    <ListItem disablePadding key={provisioning.mac}>
                      <ListItemButton
                        component={NavLink}
                        to={`${provisioning.mac}`}
                        selected={provisioning.mac === mac}
                      >
                        <ListItemAvatar>
                          <Avatar text={formatToMac(provisioning.mac)} icon={viPhoneOffice} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            provisioning.sippeer !== null
                              ? `${provisioning.sippeer.exten} \u00b7 ${provisioning.sippeer.name}`
                              : provisioning.flexDesk === 1
                                ? `${provisioning.flexBot ? provisioning.flexBot.name : "Getting ID..."} \u00b7 ${
                                    provisioning.flexUser !== null ? provisioning.flexUser.name : "No user"
                                  }`
                                : formatToMac(provisioning.mac)
                          }
                          secondary={`${provisioning.phone} ${provisioning.type}`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </CollapsibleList>
              ),
          )
        ) : (
          <ContentLoader
            count={provisioningStore.meta.count}
            loading={progressBar}
            noItems="You don't have any devices yet"
            noResults="No devices in your current query"
            fetching="Getting your devices"
            icon={viPhoneOffice}
          />
        )}
      </MasterList>
      <FabBar>
        <Button variant="contained" id="action-refresh" onClick={getRefresh} sx={{ px: 1.25, fontSize: "1rem" }}>
          <Valkyrie icon={viArrowsRotateRight} spin={refresh} />
        </Button>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viGear} />}>
          Manage
        </Button>
      </FabBar>
    </>
  );
}

export default ProvisioningList;
